import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiLong, formatFecha } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ShieldCheck, Wand2, Eye, Trash2, Upload, Undo2, Save, Loader2, ImageIcon, RefreshCcw, Flag, EyeOff, ScrollText, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_MARCO = "1) Título llamativo y corto. 2) Versículo clave. 3) Conexión empática con el dolor actual. 4) Tres puntos clave de enseñanza. 5) Verdad para meditar. 6) Oración de entrega. 7) Llamado a la acción digital.";
const DEFAULT_TONO = "Compasivo, esperanzador, pastoral, cercano. Sin triunfalismo. Valida el miedo y el duelo.";

const emptyPunto = { titulo: "", contenido: "" };

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("crear");

  // Crear
  const [textoBiblico, setTextoBiblico] = useState("");
  const [marco, setMarco] = useState(DEFAULT_MARCO);
  const [tono, setTono] = useState(DEFAULT_TONO);
  const [contexto, setContexto] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [generating, setGenerating] = useState(false);

  // Editor de borrador
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [regenImage, setRegenImage] = useState(false);

  // Listas
  const [drafts, setDrafts] = useState([]);
  const [published, setPublished] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // Moderacion
  const [reports, setReports] = useState([]);
  const [reportsStatus, setReportsStatus] = useState("open");
  const [reportsLoading, setReportsLoading] = useState(false);

  // Seguridad (auditoria)
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditActions, setAuditActions] = useState([]);
  const [auditAction, setAuditAction] = useState("all");
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSkip, setAuditSkip] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const AUDIT_PAGE = 50;

  const fetchReports = useCallback((status) => {
    setReportsLoading(true);
    api
      .get(`/admin/moderation/reports?status=${status}`)
      .then((res) => setReports(res.data.items || []))
      .catch(() => toast.error("No se pudieron cargar los reportes"))
      .finally(() => setReportsLoading(false));
  }, []);

  const fetchAudit = useCallback((action, skip) => {
    setAuditLoading(true);
    const params = new URLSearchParams({ skip: String(skip), limit: String(AUDIT_PAGE) });
    if (action && action !== "all") params.set("action", action);
    api
      .get(`/admin/security/audit?${params.toString()}`)
      .then((res) => {
        setAuditLogs(res.data.logs || []);
        setAuditActions(res.data.actions || []);
        setAuditTotal(res.data.total || 0);
      })
      .catch(() => toast.error("No se pudo cargar la auditoría"))
      .finally(() => setAuditLoading(false));
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    if (tab === "moderacion") fetchReports(reportsStatus);
    if (tab === "seguridad") fetchAudit(auditAction, auditSkip);
  }, [tab, reportsStatus, auditAction, auditSkip, user, fetchReports, fetchAudit]);

  const moderate = async (item, action) => {
    try {
      if (action === "delete") {
        await api.delete(`/admin/moderation/${item.target_type}/${item.target_id}`);
        toast.success("Contenido eliminado definitivamente");
      } else {
        await api.post(`/admin/moderation/${item.target_type}/${item.target_id}/${action}`);
        toast.success(action === "hide" ? "Contenido oculto al público" : "Contenido visible nuevamente");
      }
      fetchReports(reportsStatus);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aplicar la acción");
    }
  };

  const fetchLists = useCallback(() => {
    setListLoading(true);
    Promise.all([
      api.get("/admin/messages?status=draft"),
      api.get("/admin/messages?status=published"),
    ])
      .then(([d, p]) => {
        setDrafts(d.data.messages || []);
        setPublished(p.data.messages || []);
      })
      .catch(() => {})
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.is_admin) {
      navigate("/");
      return;
    }
    fetchLists();
  }, [user, authLoading, navigate, fetchLists]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const res = await apiLong.post("/admin/messages/generate", {
        texto_biblico: textoBiblico,
        marco,
        tono,
        contexto,
        fecha,
      });
      setDraft(res.data);
      toast.success("Borrador e Imagen del Día generados. Revísalos y publica cuando esté listo.");
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar el mensaje. Intenta de nuevo.");
    } finally {
      setGenerating(false);
    }
  };

  const updatePunto = (idx, field, value) => {
    const puntos = [...(draft.puntos_clave || [emptyPunto, emptyPunto, emptyPunto])];
    while (puntos.length < 3) puntos.push({ ...emptyPunto });
    puntos[idx] = { ...puntos[idx], [field]: value };
    setDraft({ ...draft, puntos_clave: puntos });
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        titulo: draft.titulo,
        pasaje: draft.pasaje,
        texto_biblico: draft.texto_biblico,
        oracion: draft.oracion,
        tags: draft.tags,
        fecha: draft.fecha,
      };
      if (draft.conexion !== undefined) payload.conexion = draft.conexion;
      if (draft.puntos_clave !== undefined) payload.puntos_clave = draft.puntos_clave;
      if (draft.verdad_destacada !== undefined) payload.verdad_destacada = draft.verdad_destacada;
      if (draft.llamado_accion !== undefined) payload.llamado_accion = draft.llamado_accion;
      if (draft.imagen_prompt !== undefined) payload.imagen_prompt = draft.imagen_prompt;
      const res = await api.put(`/admin/messages/${draft.id}`, payload);
      setDraft(res.data);
      toast.success("Cambios guardados");
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const regenerateImage = async () => {
    if (!draft) return;
    setRegenImage(true);
    try {
      const res = await apiLong.post(`/admin/messages/${draft.id}/generate-image`, {
        imagen_prompt: draft.imagen_prompt,
      });
      setDraft({ ...draft, has_image: true, imagen_version: res.data.imagen_version });
      toast.success("Imagen del Día regenerada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar la imagen");
    } finally {
      setRegenImage(false);
    }
  };

  const publishDraft = async (id) => {
    setPublishing(true);
    try {
      await api.post(`/admin/messages/${id}/publish`);
      toast.success("Mensaje publicado. Ya es visible para todos.");
      setDraft(null);
      fetchLists();
      setTab("publicados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo publicar");
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async (id) => {
    try {
      await api.post(`/admin/messages/${id}/unpublish`);
      toast.success("Mensaje movido a borradores");
      fetchLists();
    } catch {
      toast.error("No se pudo despublicar");
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Mensaje eliminado");
      if (draft?.id === id) setDraft(null);
      fetchLists();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  if (authLoading || !user?.is_admin) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-12">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="mt-6 h-64 w-full" />
      </div>
    );
  }

  const MessageAdminRow = ({ m, isDraft }) => (
    <Card className="border-border bg-card shadow-[var(--shadow-sm)]" data-testid={`admin-message-row-${m.id}`}>
      <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          {m.has_image && (
            <img
              src={`${BACKEND_URL}/api/messages/${m.id}/imagen?v=${m.imagen_version || 1}`}
              alt=""
              className="h-14 w-14 shrink-0 rounded-lg object-cover"
            />
          )}
          <div className="min-w-0">
            <p className="truncate font-serif text-lg font-semibold">{m.titulo}</p>
            <p className="text-sm text-muted-foreground">
              {m.pasaje} · {formatFecha(m.fecha)} ·{" "}
              <Badge variant="secondary" className={isDraft ? "bg-secondary" : "bg-accent"}>
                {isDraft ? "borrador" : "publicado"}
              </Badge>
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {isDraft ? (
            <>
              <Button size="sm" variant="outline" onClick={() => { setDraft(m); setTab("crear"); }} data-testid={`admin-edit-${m.id}`}>
                <Eye className="mr-1.5 h-4 w-4" /> Revisar
              </Button>
              <Button size="sm" onClick={() => publishDraft(m.id)} disabled={publishing} data-testid={`admin-publish-${m.id}`}>
                <Upload className="mr-1.5 h-4 w-4" /> Publicar
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => navigate(`/mensaje/${m.id}`)} data-testid={`admin-view-${m.id}`}>
                <Eye className="mr-1.5 h-4 w-4" /> Ver
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setDraft(m); setTab("crear"); }} data-testid={`admin-edit-pub-${m.id}`}>
                Editar
              </Button>
              <Button size="sm" variant="outline" onClick={() => unpublish(m.id)} data-testid={`admin-unpublish-${m.id}`}>
                <Undo2 className="mr-1.5 h-4 w-4" /> Despublicar
              </Button>
            </>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" data-testid={`admin-delete-${m.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar este mensaje?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. También se eliminarán sus comentarios y favoritos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteMessage(m.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`admin-confirm-delete-${m.id}`}>
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );

  const puntos = draft?.puntos_clave || [];

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
      <div className="mb-8 flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <h1 className="font-serif text-3xl font-semibold">Panel de Administración</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="crear" data-testid="admin-tab-crear">Crear</TabsTrigger>
          <TabsTrigger value="borradores" data-testid="admin-tab-borradores">Borradores ({drafts.length})</TabsTrigger>
          <TabsTrigger value="publicados" data-testid="admin-tab-publicados">Publicados ({published.length})</TabsTrigger>
          <TabsTrigger value="moderacion" data-testid="admin-tab-moderacion">Moderación</TabsTrigger>
          <TabsTrigger value="seguridad" data-testid="admin-tab-seguridad">Seguridad</TabsTrigger>
        </TabsList>

        <TabsContent value="crear">
          {!draft ? (
            <Card className="border-border bg-card shadow-[var(--shadow-sm)]">
              <CardContent className="p-6">
                <h2 className="mb-1 font-serif text-xl font-semibold">Nuevo mensaje con IA</h2>
                <p className="mb-6 text-sm text-muted-foreground">
                  Ingresa el texto bíblico, el marco y el tono. La IA desarrollará el mensaje con la estructura completa
                  (título · versículo clave · conexión empática · 3 puntos de enseñanza · verdad para meditar · oración ·
                  llamado a la acción) y generará la Imagen del Día compartible.
                </p>
                <form onSubmit={handleGenerate} className="flex flex-col gap-5">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="texto-biblico">Texto bíblico base *</Label>
                    <Textarea
                      id="texto-biblico"
                      required
                      value={textoBiblico}
                      onChange={(e) => setTextoBiblico(e.target.value)}
                      placeholder="Ej: Salmos 46:1-3 - Dios es nuestro amparo y fortaleza, nuestro pronto auxilio en las tribulaciones…"
                      className="min-h-[90px]"
                      data-testid="admin-generate-texto-biblico"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="marco">Marco / Estructura *</Label>
                    <Textarea
                      id="marco"
                      required
                      value={marco}
                      onChange={(e) => setMarco(e.target.value)}
                      className="min-h-[90px]"
                      data-testid="admin-generate-marco"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="tono">Tono *</Label>
                    <Textarea
                      id="tono"
                      required
                      value={tono}
                      onChange={(e) => setTono(e.target.value)}
                      className="min-h-[60px]"
                      data-testid="admin-generate-tono"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="contexto">Contexto adicional (opcional)</Label>
                      <Input
                        id="contexto"
                        value={contexto}
                        onChange={(e) => setContexto(e.target.value)}
                        placeholder="Ej: réplicas recientes, cortes de luz…"
                        data-testid="admin-generate-contexto"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="fecha">Fecha del mensaje</Label>
                      <Input
                        id="fecha"
                        type="date"
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        data-testid="admin-generate-fecha"
                      />
                    </div>
                  </div>
                  <Button type="submit" size="lg" disabled={generating} data-testid="admin-generate-submit">
                    {generating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generando mensaje e imagen… puede tardar 1-2 minutos
                      </>
                    ) : (
                      <>
                        <Wand2 className="mr-2 h-4 w-4" /> Generar borrador con IA
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border bg-card shadow-[var(--shadow-sm)]">
              <CardContent className="p-6">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold">Revisar y editar</h2>
                  <Button variant="ghost" size="sm" onClick={() => setDraft(null)} data-testid="admin-editor-back">
                    ← Nuevo mensaje
                  </Button>
                </div>
                <div className="flex flex-col gap-5">
                  {/* Imagen del Dia */}
                  <div className="flex flex-col gap-3 rounded-[var(--radius)] border border-border bg-muted/40 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                      <ImageIcon className="h-4 w-4" /> Imagen del Día (compartible)
                    </div>
                    {draft.has_image ? (
                      <img
                        src={`${BACKEND_URL}/api/messages/${draft.id}/imagen?v=${draft.imagen_version || 1}`}
                        alt="Imagen del día"
                        className="w-full max-w-md rounded-lg object-cover"
                        data-testid="admin-editor-imagen-preview"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground" data-testid="admin-editor-sin-imagen">
                        Este mensaje aún no tiene imagen. Revisa el prompt y genérala.
                      </p>
                    )}
                    <div className="flex flex-col gap-1.5">
                      <Label>Prompt de la imagen (en inglés)</Label>
                      <Textarea
                        className="min-h-[80px] bg-card"
                        value={draft.imagen_prompt || ""}
                        onChange={(e) => setDraft({ ...draft, imagen_prompt: e.target.value })}
                        data-testid="admin-editor-imagen-prompt"
                      />
                    </div>
                    <Button variant="outline" className="w-fit bg-card" onClick={regenerateImage} disabled={regenImage} data-testid="admin-editor-regenerar-imagen">
                      {regenImage ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
                      {regenImage ? "Generando imagen…" : draft.has_image ? "Regenerar imagen" : "Generar imagen"}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label>1 · Título llamativo y corto</Label>
                      <Input value={draft.titulo} onChange={(e) => setDraft({ ...draft, titulo: e.target.value })} data-testid="admin-editor-titulo" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>2 · Pasaje (cita)</Label>
                      <Input value={draft.pasaje} onChange={(e) => setDraft({ ...draft, pasaje: e.target.value })} data-testid="admin-editor-pasaje" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>2 · Versículo clave (texto completo)</Label>
                    <Textarea className="min-h-[80px]" value={draft.texto_biblico} onChange={(e) => setDraft({ ...draft, texto_biblico: e.target.value })} data-testid="admin-editor-texto-biblico" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>3 · Conexión empática (el gancho)</Label>
                    <Textarea className="min-h-[140px]" value={draft.conexion || ""} onChange={(e) => setDraft({ ...draft, conexion: e.target.value })} data-testid="admin-editor-conexion" />
                  </div>

                  <div className="flex flex-col gap-4">
                    <Label>4 · Tres puntos clave de enseñanza</Label>
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="flex flex-col gap-2 rounded-[var(--radius)] border border-border p-4">
                        <Input
                          placeholder={`Título del punto ${i + 1}`}
                          value={puntos[i]?.titulo || ""}
                          onChange={(e) => updatePunto(i, "titulo", e.target.value)}
                          data-testid={`admin-editor-punto-titulo-${i + 1}`}
                        />
                        <Textarea
                          className="min-h-[100px]"
                          placeholder={`Contenido del punto ${i + 1}`}
                          value={puntos[i]?.contenido || ""}
                          onChange={(e) => updatePunto(i, "contenido", e.target.value)}
                          data-testid={`admin-editor-punto-contenido-${i + 1}`}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label>5 · Verdad para meditar (frase destacada)</Label>
                    <Textarea className="min-h-[60px]" value={draft.verdad_destacada || ""} onChange={(e) => setDraft({ ...draft, verdad_destacada: e.target.value })} data-testid="admin-editor-verdad" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>6 · Oración de entrega / declaración</Label>
                    <Textarea className="min-h-[100px]" value={draft.oracion} onChange={(e) => setDraft({ ...draft, oracion: e.target.value })} data-testid="admin-editor-oracion" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>7 · Llamado a la acción digital</Label>
                    <Textarea className="min-h-[80px]" value={draft.llamado_accion || ""} onChange={(e) => setDraft({ ...draft, llamado_accion: e.target.value })} data-testid="admin-editor-cta" />
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label>Etiquetas (separadas por coma)</Label>
                      <Input
                        value={(draft.tags || []).join(", ")}
                        onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                        data-testid="admin-editor-tags"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>Fecha</Label>
                      <Input type="date" value={draft.fecha} onChange={(e) => setDraft({ ...draft, fecha: e.target.value })} data-testid="admin-editor-fecha" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 border-t border-border pt-5">
                    <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="admin-editor-save">
                      <Save className="mr-2 h-4 w-4" /> {saving ? "Guardando…" : "Guardar cambios"}
                    </Button>
                    {draft.status !== "published" && (
                      <Button onClick={() => publishDraft(draft.id)} disabled={publishing} data-testid="admin-editor-publish">
                        <Upload className="mr-2 h-4 w-4" /> {publishing ? "Publicando…" : "Publicar mensaje"}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="borradores">
          {listLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : drafts.length === 0 ? (
            <p className="rounded-[var(--radius)] border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="admin-drafts-empty">
              No hay borradores. Crea uno nuevo en la pestaña “Crear”.
            </p>
          ) : (
            <div className="space-y-4">
              {drafts.map((m) => (
                <MessageAdminRow key={m.id} m={m} isDraft />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="publicados">
          {listLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : published.length === 0 ? (
            <p className="rounded-[var(--radius)] border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="admin-published-empty">
              Aún no hay mensajes publicados.
            </p>
          ) : (
            <div className="space-y-4">
              {published.map((m) => (
                <MessageAdminRow key={m.id} m={m} isDraft={false} />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="moderacion">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-xl font-semibold">Contenido reportado</h2>
            </div>
            <div className="flex items-center gap-2">
              <Select value={reportsStatus} onValueChange={setReportsStatus}>
                <SelectTrigger className="h-9 w-[160px] bg-card text-sm" data-testid="admin-moderation-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Pendientes</SelectItem>
                  <SelectItem value="resolved">Resueltos</SelectItem>
                  <SelectItem value="dismissed">Descartados</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => fetchReports(reportsStatus)} data-testid="admin-moderation-refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {reportsLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : reports.length === 0 ? (
            <p className="rounded-[var(--radius)] border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="admin-moderation-empty">
              No hay reportes en esta categoría. La comunidad está tranquila.
            </p>
          ) : (
            <div className="space-y-4" data-testid="admin-moderation-list">
              {reports.map((item) => (
                <Card key={`${item.target_type}-${item.target_id}`} className="border-border bg-card shadow-[var(--shadow-sm)]" data-testid={`admin-report-${item.target_id}`}>
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="bg-accent">
                        {item.target_type === "comment" ? "Comentario" : "Petición de oración"}
                      </Badge>
                      <Badge variant="secondary" className="bg-destructive/10 text-destructive">
                        {item.report_count} {item.report_count === 1 ? "reporte" : "reportes"}
                      </Badge>
                      {item.content?.hidden && (
                        <Badge variant="secondary" className="bg-secondary">
                          <EyeOff className="mr-1 h-3 w-3" /> Oculto
                        </Badge>
                      )}
                      {!item.content && (
                        <Badge variant="secondary" className="bg-muted text-muted-foreground">Contenido eliminado</Badge>
                      )}
                    </div>
                    {item.content && (
                      <div className="mt-3 rounded-md border border-border bg-muted/40 p-3">
                        <p className="text-xs font-semibold text-muted-foreground">
                          {item.content.user_name} · {formatFecha((item.content.created_at || "").slice(0, 10))}
                        </p>
                        <p className="mt-1 whitespace-pre-line text-sm text-foreground/90">{item.content.texto}</p>
                      </div>
                    )}
                    <p className="mt-3 text-xs text-muted-foreground">
                      <span className="font-semibold">Motivos:</span> {item.motivos.join(" · ") || "sin especificar"}
                    </p>
                    {item.content && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {item.content.hidden ? (
                          <Button size="sm" variant="outline" onClick={() => moderate(item, "unhide")} data-testid={`admin-unhide-${item.target_id}`}>
                            <Eye className="mr-1.5 h-4 w-4" /> Mostrar de nuevo
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => moderate(item, "hide")} data-testid={`admin-hide-${item.target_id}`}>
                            <EyeOff className="mr-1.5 h-4 w-4" /> Ocultar
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" data-testid={`admin-mod-delete-${item.target_id}`}>
                              <Trash2 className="mr-1.5 h-4 w-4" /> Eliminar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card">
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar este contenido definitivamente?</AlertDialogTitle>
                              <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => moderate(item, "delete")}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                data-testid={`admin-mod-confirm-delete-${item.target_id}`}
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="seguridad">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-xl font-semibold">Auditoría de seguridad</h2>
            </div>
            <div className="flex items-center gap-2">
              <Select value={auditAction} onValueChange={(v) => { setAuditAction(v); setAuditSkip(0); }}>
                <SelectTrigger className="h-9 w-[230px] bg-card text-sm" data-testid="admin-audit-action-select">
                  <SelectValue placeholder="Todas las acciones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las acciones</SelectItem>
                  {auditActions.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => fetchAudit(auditAction, auditSkip)} data-testid="admin-audit-refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <p className="mb-3 text-xs text-muted-foreground" data-testid="admin-audit-total">
            {auditTotal} eventos registrados · retención 90 días
          </p>

          {auditLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : auditLogs.length === 0 ? (
            <p className="rounded-[var(--radius)] border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="admin-audit-empty">
              No hay eventos de auditoría para este filtro.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-[var(--radius)] border border-border bg-card" data-testid="admin-audit-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Recurso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(log.created_at).toLocaleString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={
                          ["ACCOUNT_LOCKED", "LOGIN_FAILED", "LOGIN_FAILED_UNKNOWN_EMAIL", "PASSWORD_CHANGE_FAILED", "LOGIN_BLOCKED_LOCKED", "CONTENT_AUTO_HIDDEN"].includes(log.action)
                            ? "bg-destructive/10 text-destructive"
                            : "bg-accent"
                        }>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[130px] truncate text-xs text-muted-foreground">{log.user_id || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{log.ip || "—"}</TableCell>
                      <TableCell className="max-w-[160px] truncate text-xs text-muted-foreground">{log.resource || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {auditTotal > AUDIT_PAGE && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" disabled={auditSkip === 0} onClick={() => setAuditSkip((s) => Math.max(0, s - AUDIT_PAGE))} data-testid="admin-audit-prev">
                Anterior
              </Button>
              <span className="text-xs text-muted-foreground">
                Página {Math.floor(auditSkip / AUDIT_PAGE) + 1} de {Math.ceil(auditTotal / AUDIT_PAGE)}
              </span>
              <Button variant="outline" size="sm" disabled={auditSkip + AUDIT_PAGE >= auditTotal} onClick={() => setAuditSkip((s) => s + AUDIT_PAGE)} data-testid="admin-audit-next">
                Siguiente
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
